import { logger } from '@origin-digital/reporting-client';
import { AxiosResponse } from 'axios';
import * as Auth0Service from '../../../services/auth0/Auth0Service';
import { getDeviceId } from '../../../url/UrlUtils';
import { tryMigrateUser } from '../migration/MigrationService';
import { lookupRegistrationUserWithCaptcha } from '../user/UserService';
import { isEmailResponseType, UserServiceResponse } from '../user/UserServiceResponse';

export interface OTCResponse {
  success: boolean;
  hasBlankEmail?: boolean;
  hasMultipleUsers?: boolean;
}

const sendOneTimeCodeRequest = async (
  username: string,
  skipUsernameCheck = false,
): Promise<OTCResponse> => {
  const otcResponse = skipUsernameCheck
    ? {
        success: true,
      }
    : await shouldSendOTC(username);
  if (otcResponse.success) {
    const deviceId = getDeviceId();
    try {
      logger.info(`Sending OTC request for username: ${username}, uniqueId: ${deviceId}`);
      await Auth0Service.triggerPasswordlessStart(username);
      return otcResponse;
    } catch (error) {
      logger.error(`Sending OTC request failed for username: ${username}, uniqueId: ${deviceId}`);
      return {
        success: error.code === 400,
      };
    }
  }
  return otcResponse;
};

const shouldSendOTC = async (username: string): Promise<OTCResponse> => {
  try {
    const userValidationResponse = await lookupRegistrationUserWithCaptcha(username);
    if (isOnlyOneUserForUsername(userValidationResponse)) {
      if (isUserIdentifierEmpty(userValidationResponse.data[0])) {
        return {
          success: false,
          hasBlankEmail: true,
          hasMultipleUsers: false,
        };
      } else {
        await tryMigrateUser(username);
        return {
          success: true,
        };
      }
    }
    return {
      success: false,
      hasMultipleUsers: userValidationResponse.data.length > 1,
    };
  } catch (e) {
    await tryMigrateUser(username);
    // Let the customer do a passwordless start if the captcha fails.  We just won't lookup their
    // username.
    return {
      success: true,
    };
  }
};

const isUserIdentifierEmpty = (userValidationResponse: UserServiceResponse) => {
  return isEmailResponseType(userValidationResponse)
    ? !userValidationResponse.email
    : !userValidationResponse.customerId;
};

const isOnlyOneUserForUsername = (userValidationResponse: AxiosResponse<[UserServiceResponse]>) => {
  return (
    typeof userValidationResponse !== 'undefined' &&
    userValidationResponse.data &&
    userValidationResponse.data.length === 1
  );
};

export { sendOneTimeCodeRequest };
